import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { Dispatch } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store.js';
import { setAccessToken, logout } from '@/features/auth/authSlice.js';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: '/api/v1',
  credentials: 'include',
  prepareHeaders: (headers, { getState, endpoint, extra, type, forced }) => {
    void endpoint;
    void extra;
    void type;
    void forced;
    headers.set('X-Correlation-Id', crypto.randomUUID());
    const state = getState() as RootState;
    const token = state.auth?.accessToken;

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    // NB: don't set Content-Type here — fetchBaseQuery sets `application/json`
    // automatically for jsonifiable bodies and leaves FormData/Blob alone so the
    // browser can supply the multipart boundary. Forcing JSON here breaks file
    // uploads (the server sees a multipart body but JSON Content-Type and 400s).
    return headers;
  },
});

// Mutex-like promise to prevent multiple parallel refresh requests
let refreshPromise: Promise<any> | null = null;

/**
 * Обновляет access-токен по refresh-cookie. Общий mutex с RTK Query, поэтому
 * параллельные 401 (в том числе от fetch/XHR мимо RTK) дают один запрос.
 * Возвращает новый токен или null (тогда пользователь уже разлогинен).
 */
export async function refreshAccessToken(dispatch: Dispatch): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = fetch('/api/v1/auth/refresh', {
      method: 'POST',
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        refreshPromise = null;
        return data;
      })
      .catch(() => {
        refreshPromise = null;
        return null;
      });
  }

  const refreshResult = await refreshPromise;

  if (refreshResult?.success && refreshResult.data?.accessToken) {
    dispatch(setAccessToken(refreshResult.data.accessToken));
    return refreshResult.data.accessToken as string;
  }
  dispatch(logout());
  dispatch(baseApi.util.resetApiState());
  return null;
}

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    const token = await refreshAccessToken(api.dispatch);
    if (token) result = await rawBaseQuery(args, api, extraOptions);
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'User',
    'Project',
    'Projects',
    'ProjectPhotos',
    'ProjectFiles',
    'ProjectActivity',
    'Room',
    'Rooms',
    'Rate',
    'Rates',
    'Estimate',
    'Estimates',
    'EstimateVersion',
    'EstimateTemplate',
    'EstimateTemplates',
    'Material',
    'Materials',
    'WarehouseItems',
    'WarehouseItem',
    'LemanaProducts',
    'Conversations',
    'Messages',
    'Tasks',
    'Task',
    'TaskComments',
    'Teams',
    'Team',
    'Notifications',
    'UserStatus',
    'CallHistory',
    'UserBranding',
    'AiUsage',
  ],
  endpoints: () => ({}),
});
